import AppLoadingAnimation from "@/_components/AppLoadingAnimation"
import JsChunksErrorBoundary from "@/_components/JsChunksErrorBoundary"
import { isAuthenticated } from "@/_services/authentication"
import { getHeaderAuthorization, resetHeaderAuthorization } from "@/_services/axios"
import { registerNotifications } from "@/_services/notification"
import { isOffline, registerOfflineStateUpdateFn, setOffline } from "@/_services/offlineService.js"
import { authenticate as authenticateWithOidc, cleanup as cleanupOidc, refreshAccessToken } from "@/_services/oidc"
import { authenticate as authenticateWithSaml, cleanup as cleanupSaml } from "@/_services/saml"
import { authenticate as authenticateWithSsoPartner } from "@/_services/ssoPartner"
import { getNoDecoration, setHostConfig, setNoDecoration, setPageTitle, toggleNavControlsDisplayMode } from "@/_services/theming"
import * as uiStateService from "@/_services/uiState"
import { canReadScripts, getIsAdmin, getOptions, getUserPermissions, setInitialUserContext } from "@/_services/userConfiguration"
import {
  aiViewToggleBtnId,
  appModes,
  checkIfInIframe,
  checkTenantConfig,
  customEvents,
  entityModelBtnId,
  globalSearchBtnId,
  isKeyboardShortcutFor,
  isNewTabOrWindowClick,
  isSameOrigin,
  keyboardShortcutActions,
  searchParamToObject,
  selfIframeMessages,
  selfIframeSources,
  setIsInSideView,
  setMarkedRendererOptions,
  setWindowId,
  sideColumnToggleId,
  softReloadRoute,
  toggleDebugMode,
  virtualKeyboardToggleBtnId,
} from "@/_services/utils"
import { pagesLayoutRoutes, rootRoutes } from "@/routes.js"
import axios from "axios"
import { frontScripts } from "basikon-common-utils"
import debounce from "lodash.debounce"
import React, { Component, Suspense, createRef } from "react"
import NotificationSystem from "react-notification-system-17"
import { Route, Switch, withRouter } from "react-router-dom"

const PagesLayout = React.lazy(() => import("@/_pages/PagesLayout") /* webpackChunkName: "precache" */)
const Page = React.lazy(() => import("@/_pages/Page"))

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      appMode: null,
      isLoadingApp: true,
      offlineSince: null,
      isInIframe: checkIfInIframe(),
    }

    setWindowId()

    this.notificationSystemRef = createRef()
  }

  /**
   * Warning: code order matters here because the execution of the function can be cut short !
   * Do not swap or add code in random places or you can break the app.
   * We could have coded things with several "if" but it would be less readable.
   */
  async componentDidMount() {
    const { isInIframe } = this.state
    const { history } = this.props
    const { location } = history || {}
    const { pathname = "", search } = location || {}
    const { userContext: initialUserContextNameBase64X2, newAuth, clearCache, useHostname, noDecoration, parentUrl } = searchParamToObject(search)

    if (["1", "true"].includes(noDecoration)) setNoDecoration(true)
    if (parentUrl) this.parentUrl = parentUrl

    // The option name is about cache because we might want one day to clear everything and not just local storage.
    if (clearCache) {
      localStorage.clear()
    }

    // This query param is used when we want to force users to login again notably those using external auth protocols.
    // Indeed when users have logged in with an auth protocol, our platform generated a token that is independent from the protocol
    // and that has its own lifetime, which we use to avoid users' browsers always going through the login page
    // (even if quickly, in which case a flash might appear) when opening a new tab.
    // To do that we check first that the token stored in localStorage is valid before deciding to direct them to the external login page.
    if (newAuth === "true") {
      resetHeaderAuthorization()
      cleanupOidc()
      cleanupSaml()
    }

    try {
      // 1. Are we resolving a short URL ?
      const shortUrlMarker = "/u/"
      const indexOfShortUrlMarker = pathname.indexOf(shortUrlMarker)
      if (indexOfShortUrlMarker === 0) {
        try {
          const {
            data: { url },
          } = await axios.get(`/api/core/key-values/${pathname.substring(shortUrlMarker.length)}?shortUrl=1`)
          if (url) return (window.location.href = url)
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }

      // The ui state must be restored before starting to load options from any source, here from the host config.
      // Otherwise the first call to setUiState might start to use an empty object due to the root of the ui state not yet being loaded.
      uiStateService.initUiState()
      const hostConfig = await setHostConfig({ useHostname })

      // 2. Is the front end allowed to be loaded on this host ?
      if (hostConfig.loadFrontEnd === false) return

      // 3. Is the app in Flow mode ?
      if (hostConfig.mode === appModes.FLOW) {
        this.setState({ appMode: hostConfig.mode, isLoadingApp: false })
        return
      }

      // 4.1. Are we using OIDC ?
      // The OIDC might redirect the user outside the app to perform the login.
      const { isRedirectingToOidcProvider } = (await authenticateWithOidc({ history })) || {}
      if (isRedirectingToOidcProvider) return

      // 4.2. Are we using SAML ?
      // The SAML might redirect the user outside the app to perform the login.
      const { isRedirectingToSamlProvider } = (await authenticateWithSaml({ history })) || {}
      if (isRedirectingToSamlProvider) return

      // 4.3. Are we using a SSO partner ?
      const { isRedirectingToSsoPartner } = (await authenticateWithSsoPartner()) || {}
      if (isRedirectingToSsoPartner) return

      // 5. Are we using offline mode ?
      registerOfflineStateUpdateFn(offlineSince => this.setState({ offlineSince }), history)
      if (!navigator.onLine) setOffline(true)

      // 6. Standard mode, code order here matters less
      document.addEventListener("keydown", this._handleKeyboardShortcuts)
      document.addEventListener("click", this.refreshAccessTokenDebounced)

      if (isInIframe) {
        document.addEventListener("contextmenu", this.handleIframeContextMenu)
        document.addEventListener("mousedown", this.handleIframeLink)

        window.addEventListener("message", this.handleSameOriginParentWindowMessage)
        window.parent.postMessage(selfIframeMessages.READY, window.location.origin)
      }

      setMarkedRendererOptions()

      if (initialUserContextNameBase64X2) {
        setInitialUserContext({ initialUserContextNameBase64X2 })
      }

      this.setState({ isLoadingApp: false })
    } catch (error) {
      // if something fails, don't render anything for better security
      console.error("App.componentDidMount()", error)
    }

    // This is a security feature to inform users of self-XSS attacks.
    this.selfXssDisclaimer()
  }

  componentDidUpdate() {
    setPageTitle()
    const { noDecoration } = searchParamToObject(window.location.search)
    if (["1", "true"].includes(noDecoration)) setNoDecoration(true)

    if (this.state.isLoadingApp === false) {
      registerNotifications(this.notificationSystemRef.current)
    }

    refreshAccessToken()
  }

  componentWillUnmount() {
    const { isInIframe } = this.state
    document.removeEventListener("keydown", this._handleKeyboardShortcuts)
    document.removeEventListener("click", this.refreshAccessTokenDebounced)

    if (isInIframe) {
      document.removeEventListener("contextmenu", this.handleIframeContextMenu)
      document.removeEventListener("mousedown", this.handleIframeLink)

      window.removeEventListener("message", this.handleSameOriginParentWindowMessage)
    }
  }

  // Note that this function is called manually in other places that want to ensure
  // that the token is fresh before continuing.
  // That is why we don't use the event option useCapture along with removing other invocations of this function.
  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#usecapture
  refreshAccessTokenDebounced = debounce(refreshAccessToken, 1000)

  _handleKeyboardShortcuts = event => this.handleKeyboardShortcuts(event)

  selfXssDisclaimer = () => {
    if (window?.console) {
      console.log("%cSTOP!", "color: red; font-size: 50px; font-weight: bold; text-shadow: 2px 2px white;")
      console.log(
        "%cWarning: This browser feature is intended for developers. " +
          "If anyone outside of our Support team asks you to paste something here - whether to enable a feature or for any other reason—it may be an attempt to gain access to your account. " +
          "If you are unsure, do not proceed. Contact Support immediately. ",
        "font-size: 16px;",
      )
      console.log("%cSee https://en.wikipedia.org/wiki/Self-XSS for more information.", "font-size: 16px;")
    }
  }

  handleSameOriginParentWindowMessage = event => {
    const { history } = this.props
    const { origin, data } = event

    // Security check: the app can only embed itself when not in Flow mode.
    // Note that the Page system (see Page.jsx) also does this check but it can be configured,
    // whereas here we don't allow configuration.
    if (origin !== window.location.origin) return

    try {
      const parentWindowData = typeof data === "string" ? JSON.parse(data) : typeof data === "object" ? data : {}
      const { url, source } = parentWindowData

      // Another check, but this time for app maintenance so we know where messages come from
      // and also to write code only needed for that source.
      if (source === selfIframeSources.SIDE_VIEW) {
        setIsInSideView(true)
        if (url) {
          history.replace(url)
          document.getElementsByTagName("body")[0].scroll({
            top: 0,
            left: 0,
            behavior: "smooth",
          })
        }
        return
      }
    } catch (error) {
      console.log("The parent window message is not a JSON")
    }
  }

  handleKeyboardShortcuts = event => {
    if (!isAuthenticated()) return

    refreshAccessToken()

    const { isInIframe } = this.state
    const { history } = this.props
    const canOpenQueryToken = canReadScripts() || getOptions("allowOpenQueryToken")
    const isAdmin = getIsAdmin()
    const allowDebugMode = getOptions("allowDebugMode") ?? true
    // Users connected without any permissions (i.e. without any profile) must be restricted
    // this can happen especially when connecting through OIDC with bad checks on app access.
    const userPermissions = getUserPermissions()
    const hasPermissions = Object.keys(userPermissions || {}).length > 0

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.softReloadRoute })) {
      softReloadRoute({ event, behavior: "tryReloadData" })
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleNavControlsDisplayMode })) {
      toggleNavControlsDisplayMode()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.checkTenantConfig }) && (hasPermissions || isAdmin)) {
      checkTenantConfig()
    }

    // this is for debug purpose
    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleOfflineMode })) {
      setOffline(!isOffline(), true)
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleDebugMode }) && (hasPermissions || isAdmin) && allowDebugMode) {
      // disable this combination on the login page because this is the "add bookmark" shortcut
      // for major browsers (Chrome, Firefox) and thus would require the user to focus its mouse
      // elsewhere to trigger it
      if (window.location.pathname === "/authentication/login-page") return
      toggleDebugMode(history)
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.clickOnGlobalSearchBtn })) {
      document.getElementById(globalSearchBtnId)?.click()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.goToScriptsPage }) && canReadScripts()) {
      history.push("/scripts")
    }

    // Submit form
    // Warning : this applies to any first button of type submit found in the current page
    // so if you have multiple submit button, place first in the DOM the one you want to be
    // triggered when using this keyboard combination.
    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.save })) {
      const submitBtn = document.querySelector('button[type="submit"]')
      if (submitBtn) submitBtn.click()
    }

    // collapse all cards
    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.collapseAllCards })) {
      window.dispatchEvent(new CustomEvent(customEvents.card.toggle))
      window.dispatchEvent(new CustomEvent(customEvents.innerPanel.toggle))
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.openQueryToken }) && canOpenQueryToken) {
      const token = getHeaderAuthorization()?.substring("bearer ".length)
      if (!token) return
      const url = `${window.location.origin}/api/script/runs/${frontScripts.helloWorld}?token=${token}`
      const win = window.open(url, "_blank")
      win.focus()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleSettings })) {
      document.getElementById(sideColumnToggleId)?.click()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleVirtualKeyboard })) {
      document.getElementById(virtualKeyboardToggleBtnId)?.click()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.showEntityModelModal })) {
      document.getElementById(entityModelBtnId)?.click()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleNoDecorationMode }) && !isInIframe) {
      setNoDecoration(!getNoDecoration())
      window.dispatchEvent(new Event("resize"))
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.clickOnAiViewToggleBtn }) && !isInIframe) {
      document.getElementById(aiViewToggleBtnId)?.click()
    }

    if (isKeyboardShortcutFor({ event, action: keyboardShortcutActions.toggleSideView })) {
      if (isInIframe) {
        window.parent.postMessage(customEvents.sideView.toggleView, window.location.origin)
      } else {
        window.dispatchEvent(new CustomEvent(customEvents.sideView.toggleView))
      }
    }
  }

  findLink = event => {
    let link
    const linkIndex = event.composedPath().findIndex(it => it.tagName?.toLowerCase() === "a")
    if (event.target.tagName.toLowerCase() === "a") link = event.target
    else if (linkIndex > 0) {
      let tmp = event.target.parentNode
      for (let i = 1; i <= linkIndex; i++) {
        if (tmp.tagName.toLowerCase() === "a") link = tmp
        else tmp = tmp.parentNode
      }
    }
    return link
  }

  /**
   * When the app is embedded in a iframe
   * if the user choose to open a link in a new tab
   * by opening the context menu with a right-click then choosing "open in a new tab" option
   * we force the link to point to the parent url (of the iframe) so that the user cannot "exit" the iframe
   */
  handleIframeContextMenu = event => {
    const link = this.findLink(event)
    if (link && this.parentUrl && !link.href.includes(this.parentUrl)) {
      link.href = `${this.parentUrl}?basikOnPath=${link.href.replace(window.location.origin, "")}`
    }
  }

  /**
   * Same as handleIframeContextMenu but for other means of opening a link not in the current view :
   * - new tab with click + ctrl (or cmd for mac)
   * - new window with click + shift
   * - new tab middle click (scroll wheel)
   */
  handleIframeLink = event => {
    const link = this.findLink(event)
    if (!link) return

    if (isNewTabOrWindowClick(event)) {
      if (this.parentUrl && !link.href.includes(this.parentUrl)) {
        link.href = `${this.parentUrl}?basikOnPath=${link.href.replace(window.location.origin, "")}`
      }
    } else if (link.href?.startsWith("http") && isSameOrigin()) {
      // This handles opening absolute links from within the SideView.
      // Instead of opening them in place which breaks the iframe, we force them to open a new tab.
      link.target = "_blank"
      link.rel = "noopener noreferrer"
    }
  }

  render() {
    const { appMode, isLoadingApp, offlineSince } = this.state

    if (isLoadingApp) return <AppLoadingAnimation />

    const routeContent =
      appMode === appModes.FLOW ? (
        <Route
          path="/k/:accessKey/p/:pageId"
          render={routeProps => (
            <Suspense fallback={null}>
              <Page {...routeProps} appMode={appMode} />
            </Suspense>
          )}
        />
      ) : (
        <Route
          path="/"
          render={routeProps => {
            const { location } = routeProps

            for (let i = 0; i < rootRoutes.length; i++) {
              const rootRoute = rootRoutes[i]
              if (location.pathname.startsWith(rootRoute.path)) return rootRoute.render()
            }

            return (
              <Suspense fallback={null}>
                <PagesLayout {...routeProps} routes={pagesLayoutRoutes} offlineSince={offlineSince} />
              </Suspense>
            )
          }}
        />
      )

    return (
      <JsChunksErrorBoundary>
        <NotificationSystem ref={this.notificationSystemRef} />
        <Suspense fallback={null}>
          <Switch>{routeContent}</Switch>
        </Suspense>
      </JsChunksErrorBoundary>
    )
  }
}

export default withRouter(App)
